import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropertyCard from "./PropertyCard";
import FilterPills from "../components/FilterPills";
import ActiveFilterChips from "../components/ActiveFilterChips";
import { PropertyGridSkeleton, PropertyCardSkeleton } from "../components/Skeletons";
import {
  Lock, Crown, Sparkles, ArrowRight, Phone, MessageSquare, Star, CheckCircle2, Boxes,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useConfig } from "../lib/ConfigContext";
import { useAuth } from "../lib/AuthContext";
import { appendCustomFilterParams } from "../lib/filterUtils";
import { filtersToQuery, queryToFilters, queryToPage } from "../lib/filterUrl";
import { useProperties } from "../features/property/hooks/useProperties";

// v2.87 Phase 3b — DealerExplore migrated from legacy MobileFilterSheet →
// unified FilterPills + paginated /api/properties/search (same UX as the
// public Dashboard). Adds intent chips, multi-select pills, ActiveFilterChips,
// and URL persistence. The dealer's own listings are excluded via the new
// `exclude_submitted_by_id` server-side filter.

const defaultFilters = () => ({
  priceRange: [500000, 300000000],
  main_area: "", sub_area: "", area: "", bhk: "",
  listing_type: "", property_type: "",
  maxMetro: 3000, tag: "",
  listing_intent: "",
  posted_by: "",
  furnishing: "",
  possession_status: "",
  min_size: "",
  max_size: "",
  verified: false,
  sort: "",
});

// Build the active filter chip list (mirrors Dashboard.buildActiveChips).
const buildActiveChips = (filters, configFilters, setFilters, defaults) => {
  const chips = [];
  const intentLabel = { sale: "Sale", rent: "Rent", lease: "Lease" };
  if (filters.listing_intent) {
    chips.push({
      key: "listing_intent",
      label: intentLabel[filters.listing_intent] || filters.listing_intent,
      onRemove: () => setFilters((p) => ({ ...p, listing_intent: "" })),
    });
  }
  if (filters.listing_type) {
    chips.push({
      key: "listing_type",
      label: filters.listing_type,
      onRemove: () => setFilters((p) => ({ ...p, listing_type: "" })),
    });
  }
  const _pushCsv = (key, csv, onSet) => {
    const parts = String(csv || "").split(",").map((x) => x.trim()).filter(Boolean);
    parts.forEach((val) => {
      chips.push({
        key, value: val, label: val,
        onRemove: () => onSet(parts.filter((x) => x !== val).join(",")),
      });
    });
  };
  _pushCsv("property_type", filters.property_type,
    (next) => setFilters((p) => ({ ...p, property_type: next })));
  _pushCsv("bhk", filters.bhk,
    (next) => setFilters((p) => ({ ...p, bhk: next })));
  _pushCsv("furnishing", filters.furnishing,
    (next) => setFilters((p) => ({ ...p, furnishing: next })));
  // v2.87 Phase 3b ext — Main Area multi-select + Tag multi-select.
  _pushCsv("main_area", filters.main_area,
    (next) => setFilters((p) => ({ ...p, main_area: next, sub_area: "" })));
  if (filters.sub_area) {
    chips.push({
      key: "sub_area", label: filters.sub_area,
      onRemove: () => setFilters((p) => ({ ...p, sub_area: "" })),
    });
  }
  if (filters.possession_status) {
    const possLabels = { ready_to_move: "Ready to Move", under_construction: "Under Construction", new_launch: "New Launch" };
    chips.push({
      key: "possession_status",
      label: possLabels[filters.possession_status] || filters.possession_status,
      onRemove: () => setFilters((p) => ({ ...p, possession_status: "" })),
    });
  }
  _pushCsv("tag", filters.tag,
    (next) => setFilters((p) => ({ ...p, tag: next })));
  if (filters.posted_by) {
    chips.push({
      key: "posted_by", label: `Posted by ${filters.posted_by}`,
      onRemove: () => setFilters((p) => ({ ...p, posted_by: "" })),
    });
  }
  if (filters.verified) {
    chips.push({
      key: "verified", label: "Verified",
      onRemove: () => setFilters((p) => ({ ...p, verified: false })),
    });
  }
  const pr = filters.priceRange;
  const dpr = defaults?.priceRange || [500000, 300000000];
  if (pr && (pr[0] !== dpr[0] || pr[1] !== dpr[1])) {
    const fmt = (n) => n >= 10000000 ? `₹${(n / 10000000).toFixed(n % 10000000 === 0 ? 0 : 1)} Cr` :
                      n >= 100000 ? `₹${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)} L` :
                      n >= 1000 ? `₹${(n / 1000).toFixed(0)}K` : `₹${n}`;
    const intent = filters.listing_intent || "sale";
    const lbl = intent === "rent" ? "Rent" : intent === "lease" ? "Lease" : "Price";
    chips.push({
      key: "price",
      label: pr[0] === 0 ? `${lbl}: up to ${fmt(pr[1])}` : `${lbl}: ${fmt(pr[0])} – ${fmt(pr[1])}`,
      onRemove: () => setFilters((p) => ({ ...p, priceRange: dpr })),
    });
  }
  if (filters.min_size || filters.max_size) {
    const part = (filters.min_size && filters.max_size) ? `${filters.min_size}–${filters.max_size}`
                : filters.min_size ? `≥ ${filters.min_size}` : `≤ ${filters.max_size}`;
    chips.push({
      key: "size", label: `Size ${part}`,
      onRemove: () => setFilters((p) => ({ ...p, min_size: "", max_size: "" })),
    });
  }
  const defMetro = defaults?.maxMetro ?? 3000;
  if (filters.maxMetro && filters.maxMetro !== defMetro) {
    chips.push({
      key: "metro", label: `Metro ≤ ${filters.maxMetro}m`,
      onRemove: () => setFilters((p) => ({ ...p, maxMetro: defMetro })),
    });
  }
  if (filters.sort) {
    const sortLabels = { "created_at:desc": "Newest", "price:asc": "Price ↑", "price:desc": "Price ↓", "distance_metro_m:asc": "Closest metro" };
    chips.push({
      key: "sort", label: `Sort: ${sortLabels[filters.sort] || filters.sort}`,
      onRemove: () => setFilters((p) => ({ ...p, sort: "" })),
    });
  }
  for (const f of configFilters || []) {
    if (!f.key || ["price", "bhk", "property_type", "listing_type", "main_area", "sub_area", "metro_distance", "size", "tag", "furnishing"].includes(f.key)) continue;
    const v = filters[f.key];
    if (v === undefined || v === "" || v === null || v === false) continue;
    if (Array.isArray(v)) continue;
    chips.push({
      key: f.key, label: `${f.name || f.key}: ${v}`,
      onRemove: () => setFilters((p) => ({ ...p, [f.key]: "" })),
    });
  }
  return chips;
};

export default function DealerExplore({ hasActivePlan, onUpgradeClick }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { filters: configFilters = [] } = useConfig();
  const { user } = useAuth();

  // Hydrate filters from URL on mount.
  const [filters, setFilters] = useState(() => queryToFilters(window.location.search, defaultFilters()));
  const skipNextUrlSync = useRef(false);

  // Build filter params for /api/properties/search.
  const searchFilters = useMemo(() => {
    const p = {};
    if (filters.main_area) p.main_area = filters.main_area;
    if (filters.sub_area) p.sub_area = filters.sub_area;
    if (filters.area && !filters.main_area) p.area = filters.area;
    if (filters.bhk) p.bhk = filters.bhk;
    if (filters.listing_type) p.listing_type = filters.listing_type;
    if (filters.listing_intent) p.listing_intent = filters.listing_intent;
    if (filters.property_type) p.property_type = filters.property_type;
    if (filters.priceRange?.[0] > 1000000)
      p.min_price = Math.max(p.min_price || 0, filters.priceRange[0]);
    if (filters.priceRange?.[1] < 30000000)
      p.max_price = filters.priceRange[1];
    if (filters.maxMetro < 3000) p.max_metro_distance = filters.maxMetro;
    if (filters.tag) p.tag = filters.tag;
    if (filters.posted_by) p.posted_by = filters.posted_by;
    if (filters.furnishing) p.furnishing = filters.furnishing;
    if (filters.possession_status) p.possession_status = filters.possession_status;
    if (filters.min_size) p.min_size = filters.min_size;
    if (filters.max_size) p.max_size = filters.max_size;
    if (filters.verified) p.verified = true;
    // Dealer marketplace contract — exclude the dealer's own listings.
    if (user?.id) p.exclude_submitted_by_id = user.id;
    appendCustomFilterParams(p, filters, configFilters);
    return p;
  }, [filters, configFilters, user?.id]);

  const currentSort = filters.sort || "created_at:desc";
  const initialPage = useMemo(() => queryToPage(window.location.search), []);

  const {
    items: properties,
    total,
    isLoading,
    isLoadingMore,
    hasMore,
    page,
    sentinelRef,
  } = useProperties({
    filters: searchFilters,
    pageSize: 24,
    sort: currentSort,
    initialPage,
  });

  // Sync filters → URL (replace history entry so back-button undoes filter steps).
  useEffect(() => {
    if (skipNextUrlSync.current) { skipNextUrlSync.current = false; return; }
    const qs = filtersToQuery(filters, defaultFilters(), { page });
    const target = `${loc.pathname}${qs ? `?${qs}` : ""}`;
    const current = `${loc.pathname}${loc.search}`;
    if (target !== current) nav(target, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  // React to back/forward navigation: re-hydrate filter state from URL.
  useEffect(() => {
    const fromUrl = queryToFilters(loc.search, defaultFilters());
    const same = JSON.stringify(fromUrl) === JSON.stringify(filters);
    if (!same) {
      skipNextUrlSync.current = true;
      setFilters(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.search]);

  return (
    <div className="space-y-5" data-testid="dealer-explore-tab">
      {!hasActivePlan && (
        <div className="bg-gradient-to-br from-[#0055FF] to-[#003ECC] text-white border border-[#0044CC] rounded-sm p-5 lg:p-6" data-testid="dealer-upgrade-banner">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex-1 min-w-[260px]">
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-white/70 mb-2 inline-flex items-center gap-1.5">
                <Crown size={11} /> Dealer Pro · Premium features locked
              </div>
              <h3 className="font-heading font-bold text-2xl lg:text-3xl tracking-tight">Unlock contact details, leads inbox, and priority placement.</h3>
              <p className="text-[13px] text-white/80 mt-2 max-w-2xl">
                Browsing is always free. To call clients directly, view leads, and rank higher in search — activate a Dealer plan.
              </p>
            </div>
            <Button
              onClick={onUpgradeClick}
              className="bg-white text-[#0055FF] hover:bg-white/95 font-semibold rounded-sm h-11 px-6"
              data-testid="dealer-upgrade-cta"
            >
              <Sparkles size={14} className="mr-1.5" /> See Plans <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-5 border-t border-white/20">
            <LockChip icon={<Phone size={11} />} label="Owner contacts" />
            <LockChip icon={<MessageSquare size={11} />} label="Leads inbox" />
            <LockChip icon={<Star size={11} />} label="Priority listing" />
            <LockChip icon={<CheckCircle2 size={11} />} label="Verified badge" />
          </div>
        </div>
      )}

      {hasActivePlan && (
        <div className="bg-[#D1FAE5] border border-[#A7F3D0] text-[#065F46] rounded-sm p-3 inline-flex items-center gap-2" data-testid="dealer-plan-active-banner">
          <CheckCircle2 size={14} /> <b>Plan active</b> — all premium features unlocked.
        </div>
      )}

      {/* v2.87 Phase 3b — Unified FilterPills strip + active chips (replaces
          the legacy MobileFilterSheet / FilterPanel sidebar). */}
      <div
        className="sticky top-[48px] sm:top-[52px] lg:top-[56px] z-20 -mx-3 px-3 sm:mx-0 sm:px-0 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/75 border-b border-slate-100"
        data-results-anchor="filter-strip"
      >
        <FilterPills
          filters={filters}
          setFilters={setFilters}
          onReset={() => setFilters(defaultFilters())}
          configFilters={configFilters}
          defaults={defaultFilters()}
          testidPrefix="de-fp"
        />
      </div>

      <ActiveFilterChips
        chips={buildActiveChips(filters, configFilters, setFilters, defaultFilters())}
        onClearAll={() => setFilters(defaultFilters())}
        testidPrefix="de-afc"
      />

      <main>
        <div className="flex items-end justify-between mb-5 pb-3 border-b border-[#E4E4E7]">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#52525B] mb-1 inline-flex items-center gap-1.5">
              <Boxes size={11} /> Marketplace feed
            </div>
            <h2 className="font-heading font-semibold text-2xl tracking-tight text-[#09090B]">
              <span data-testid="explore-result-count">{properties.length}</span>
              {total > properties.length && <span className="text-[#A1A1AA]"> of {total}</span>} listings
            </h2>
            <div className="text-[11px] text-[#52525B] mt-1 inline-flex items-center gap-1">
              <Lock size={9} /> Exact location & owner hidden — premium plans unlock full access
            </div>
          </div>
        </div>
        {isLoading ? (
          <PropertyGridSkeleton count={6} />
        ) : properties.length === 0 ? (
          <div className="text-center py-20 text-[#A1A1AA] border border-dashed border-[#E4E4E7] rounded-sm" data-testid="explore-empty">
            No listings match your filters.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="explore-grid">
              {properties.map((p, i) => (
                <div key={p.id} className="relative">
                  <PropertyCard p={p} index={i} />
                  {!hasActivePlan && (
                    <div className="absolute top-2 right-2 bg-[#09090B]/80 backdrop-blur-sm text-white text-[9px] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm inline-flex items-center gap-1 pointer-events-none" data-testid={`locked-overlay-${p.id}`}>
                      <Lock size={9} /> Plan needed for contact
                    </div>
                  )}
                </div>
              ))}
              {isLoadingMore && Array.from({ length: 3 }).map((_, i) => (
                <PropertyCardSkeleton key={`loading-more-${i}`} />
              ))}
            </div>
            {hasMore && (
              <div
                ref={sentinelRef}
                aria-hidden="true"
                className="h-2 w-full"
                data-testid="properties-infinite-sentinel"
              />
            )}
            {!hasMore && properties.length >= 24 && (
              <div className="text-center text-[12px] text-[#A1A1AA] py-8" data-testid="properties-end-of-list">
                You&apos;ve reached the end of the list.
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

const LockChip = ({ icon, label }) => (
  <div className="bg-white/10 border border-white/20 rounded-sm px-3 py-2 text-[11px] inline-flex items-center gap-2">
    <Lock size={10} /> {icon} <span className="font-semibold">{label}</span>
  </div>
);
